import { createStore, type StoreApi } from 'zustand/vanilla';

import { createEmptyTree, type ReducerError, type SessionTree } from '../types/tree';
import type { LangchainAgentEvent, TierOverrides } from '../types/events';
import { markEventIdSeen, shouldSkipDuplicateEvent } from './dedupe';
import { reduceTree, type ReduceOptions } from './reducer';
import type { SourceEventContext } from '../transport/types';

export interface SessionStoreOptions {
  tierOverrides?: TierOverrides;
  debug?: boolean;
  /** 预灌历史事件数；续流时保证 reducer eventIndex 连续 */
  initialEventCount?: number;
  /** 续传恢复：已见过的 SSE id，避免重叠帧重复建节点 */
  initialSeenEventIds?: Iterable<string>;
}

export interface SessionStoreState {
  tree: SessionTree;
  eventCount: number;
  applyEvent: (event: LangchainAgentEvent, ctx?: SourceEventContext) => void;
  applyEvents: (events: LangchainAgentEvent[]) => void;
  reset: () => void;
  markAsError: (error?: Error) => void;
}

function safeReduce(
  tree: SessionTree,
  event: LangchainAgentEvent,
  eventIndex: number,
  options?: ReduceOptions,
): { tree: SessionTree; error?: ReducerError } {
  try {
    return { tree: reduceTree(tree, event, eventIndex, options) };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    if (import.meta.env?.DEV) {
      console.error('[langchain_agentx_stream_ui] reducer error:', err);
    }
    return {
      tree: {
        ...tree,
        internalErrors: [
          ...tree.internalErrors,
          { eventIndex, eventType: event.event_type, message, stack },
        ],
      },
    };
  }
}

export function createSessionStore(
  initialTree: SessionTree = createEmptyTree(),
  storeOptions?: SessionStoreOptions,
): StoreApi<SessionStoreState> {
  const reduceOpts: ReduceOptions = {
    tierOverrides: storeOptions?.tierOverrides,
    collectDebug: storeOptions?.debug === true,
  };
  const initialEventCount = storeOptions?.initialEventCount ?? 0;
  const seenEventIds = new Set<string>(storeOptions?.initialSeenEventIds);
  return createStore<SessionStoreState>((set, get) => ({
    tree: initialTree,
    eventCount: initialEventCount,
    applyEvent(event, ctx) {
      const sseEventId = ctx?.sseEventId;
      if (shouldSkipDuplicateEvent(seenEventIds, sseEventId)) return;

      const { tree, eventCount } = get();
      const { tree: reduced } = safeReduce(tree, event, eventCount, reduceOpts);
      const nextTree: SessionTree =
        sseEventId
          ? {
              ...reduced,
              meta: { ...reduced.meta, lastEventId: sseEventId },
            }
          : reduced;
      markEventIdSeen(seenEventIds, sseEventId);
      set({ tree: nextTree, eventCount: eventCount + 1 });
    },
    applyEvents(events) {
      let { tree, eventCount } = get();
      for (const event of events) {
        const result = safeReduce(tree, event, eventCount, reduceOpts);
        tree = result.tree;
        eventCount += 1;
      }
      set({ tree, eventCount });
    },
    reset() {
      seenEventIds.clear();
      set({ tree: createEmptyTree(), eventCount: 0 });
    },
    markAsError(error?: Error) {
      const { tree } = get();
      set({
        tree: {
          ...tree,
          status: 'error',
          internalErrors: error
            ? [
                ...tree.internalErrors,
                {
                  eventIndex: -1,
                  eventType: 'sse_connection_failed',
                  message: error.message,
                  stack: error.stack,
                },
              ]
            : tree.internalErrors,
        },
      });
    },
  }));
}

export type SessionStore = StoreApi<SessionStoreState>;
